/**
 * Subscription Payment Modal
 * Pricing model = single source of truth (GET /api/config/pricing):
 *   Platform SOFTWARE is 100% FREE. Slab/tier pricing by student count.
 *   Boundary rule: upper bound inclusive (300 → tier1, 301 → tier2).
 *   First payment = one_time (license + setup) + first-year maintenance.
 *   5000+ students = custom → contact sales on WhatsApp (no online checkout).
 */
import React, { useEffect, useState } from 'react';
import { useRazorpayPayment } from '../hooks/useRazorpay';
import { usePricingConfig } from '../hooks/usePricingConfig';
import { trackPublicCTA } from '../utils/publicFunnelTracking';
import { trackPinterest } from '../utils/pinterestTag';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SubscriptionPaymentModal({
  isOpen,
  onClose,
  language = 'hi',
  prefillData = null,
  startStep = 1,
  source = 'public_payment_modal',
}) {
  const { paySubscription, loading } = useRazorpayPayment();
  const { config: pricing } = usePricingConfig(); // SSOT with local fallback tiers

  const [step, setStep] = useState(1); // 1: Students, 2: Details, 3: Payment
  const [studentCount, setStudentCount] = useState('');
  const [formData, setFormData] = useState({ schoolName: '', contactName: '', contactPhone: '', contactEmail: '' });
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [provisionData, setProvisionData] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setStep(startStep || 1);
    setStudentCount(prefillData?.studentCount ? String(prefillData.studentCount) : '');
    setProvisionData(null);
    setPaymentSuccess(false);
    setPaymentError(null);
    if (prefillData) {
      setFormData({
        schoolName: prefillData.schoolName || prefillData.school || '',
        contactName: prefillData.contactName || prefillData.name || '',
        contactPhone: prefillData.contactPhone || prefillData.phone || '',
        contactEmail: prefillData.contactEmail || prefillData.email || '',
      });
    }
    trackPublicCTA({
      source_page: source,
      cta_id: `${source}_open`,
      cta_label: 'Open Payment Modal',
      destination: 'payment_modal',
      session_id: prefillData?.chatSessionId || null,
      phone: prefillData?.contactPhone || prefillData?.phone || null,
      email: prefillData?.contactEmail || prefillData?.email || null,
    });
  }, [isOpen, prefillData, startStep, source]);

  if (!isOpen) return null;

  const sym = pricing?.currency_symbol || '₹';
  const tiers = pricing?.pricing_tiers || [];
  const salesPhone = pricing?.sales_contact_phone || '9826618603';

  const count = parseInt(studentCount, 10) || 0;
  const tier = count > 0
    ? tiers.find((t) => count >= t.min && (t.max == null || count <= t.max))
    : null;
  const isCustom = !!tier?.custom;
  const oneTime = tier && !isCustom ? tier.one_time : 0;
  const maint = tier && !isCustom ? tier.annual_maintenance : 0;
  const total = oneTime + maint; // first payment = one-time + first-year maintenance

  const fmt = (n) => `${sym}${Number(n).toLocaleString('en-IN')}`;

  const salesWhatsappUrl = () => {
    const msg = language === 'hi'
      ? `नमस्ते! हमारे स्कूल में ${count || '5000+'} छात्र हैं। कृपया Shiksha Mitra के custom pricing की जानकारी दें।`
      : `Hi! Our school has ${count || '5000+'} students. Please share Shiksha Mitra custom pricing details.`;
    return `https://wa.me/91${salesPhone}?text=${encodeURIComponent(msg)}`;
  };

  const handleContactSales = () => {
    trackPublicCTA({
      source_page: source,
      cta_id: `${source}_contact_sales`,
      cta_label: 'Contact Sales (5000+ custom)',
      destination: 'whatsapp_sales',
      session_id: prefillData?.chatSessionId || null,
      phone: formData.contactPhone || null,
      email: formData.contactEmail || null,
      meta: { studentCount: count },
    });
    window.open(salesWhatsappUrl(), '_blank');
  };

  const handlePayment = async () => {
    if (!formData.schoolName || !formData.contactName || !formData.contactPhone) {
      setPaymentError(language === 'hi' ? 'कृपया सभी ज़रूरी fields भरें' : 'Please fill all required fields');
      return;
    }
    if (count < 1) {
      setPaymentError(language === 'hi' ? 'कृपया छात्रों की संख्या भरें' : 'Please enter number of students');
      return;
    }
    if (isCustom) {
      handleContactSales();
      return;
    }
    setPaymentError(null);
    trackPublicCTA({
      source_page: source,
      cta_id: `${source}_submit`,
      cta_label: 'Initiate Payment',
      destination: 'razorpay_checkout',
      session_id: prefillData?.chatSessionId || null,
      phone: formData.contactPhone,
      email: formData.contactEmail,
      meta: { schoolName: formData.schoolName, studentCount: count, oneTime, maint, total },
    });

    await paySubscription({
      schoolId: `school_${Date.now()}`,
      planId: 'standard',
      planDuration: 'one_time_plus_maintenance',
      studentCount: count,
      schoolName: formData.schoolName,
      contactName: formData.contactName,
      contactPhone: formData.contactPhone,
      contactEmail: formData.contactEmail,
      chatSessionId: prefillData?.chatSessionId,
      source: prefillData?.chatSessionId ? 'aayush_payment_modal' : 'public_payment_modal',
      onSuccess: async (data) => {
        setProvisionData(data?.autoProvision || null);
        if (prefillData?.chatSessionId) {
          try {
            await fetch(`${API_URL}/api/live-chat/update-stage`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                session_id: prefillData.chatSessionId,
                stage: 'onboarding_pending',
                metadata: {
                  school_name: formData.schoolName,
                  principal_name: formData.contactName,
                  payment_reference: data?.paymentId || '',
                },
              }),
            });
          } catch (err) { console.error('Failed to sync Aayush payment stage:', err); }
        }
        setPaymentSuccess(true);
        trackPinterest('checkout', {
          value: data?.amount || total,
          order_quantity: 1,
          currency: 'INR',
          line_items: [{ product_name: 'Shiksha Mitra Subscription', product_category: 'subscription', product_price: data?.amount || total }],
        }, { email: formData.contactEmail, phone: formData.contactPhone });
        setTimeout(() => { onClose(); setPaymentSuccess(false); setStep(1); }, 3500);
      },
      onFailure: (err) => {
        if (err.message !== 'Payment cancelled') setPaymentError(err.message || err.description || 'Payment failed');
      },
    });
  };

  const canContinueStep1 = count >= 1;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" data-testid="subscription-payment-modal">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-orange-500 to-amber-500 text-white p-6 rounded-t-3xl z-10">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">{language === 'hi' ? '🎉 Subscription चालू करें' : '🎉 Activate Subscription'}</h2>
              <p className="text-orange-100 text-sm mt-1" data-testid="modal-free-tagline">
                {language === 'hi' ? 'Software 100% FREE · Plan आपके छात्रों की संख्या पर आधारित' : 'Software 100% FREE · Plan based on your student count'}
              </p>
            </div>
            <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition" data-testid="subscription-payment-close">
              <span className="text-xl">✕</span>
            </button>
          </div>
          <div className="flex items-center justify-center gap-2 mt-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition ${step >= s ? 'bg-white text-orange-500' : 'bg-white/30 text-white'}`}>{step > s ? '✓' : s}</div>
                {s < 3 && <div className={`w-12 h-1 mx-1 rounded ${step > s ? 'bg-white' : 'bg-white/30'}`}></div>}
              </div>
            ))}
          </div>
        </div>

        <div className="p-6">
          {paymentSuccess ? (
            <div className="text-center py-12">
              <div className="text-7xl mb-6 animate-bounce">🎊</div>
              <h3 className="text-2xl font-bold text-green-600 mb-2">{language === 'hi' ? 'भुगतान सफल!' : 'Payment Successful!'}</h3>
              <p className="text-gray-600">{language === 'hi' ? 'आपका subscription activate हो गया है। Login credentials और onboarding checklist नीचे दी गई है।' : 'Your subscription is now active. Login credentials and onboarding checklist are shown below.'}</p>
              {provisionData && (
                <div className="mt-6 text-left rounded-2xl border border-green-200 bg-green-50 p-5" data-testid="auto-provision-summary-card">
                  <h4 className="text-lg font-bold text-green-800 mb-3">School Auto-Create Complete</h4>
                  <div className="space-y-1 text-sm text-green-900">
                    <p><strong>School:</strong> {provisionData.school_name}</p>
                    <p><strong>Subdomain:</strong> {provisionData.school_subdomain}</p>
                    <p><strong>Login URL:</strong> {provisionData.login_url}</p>
                    <p><strong>Phone:</strong> {provisionData.login_phone}</p>
                    <p><strong>Password:</strong> {provisionData.temporary_password}</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Step 1: Student count → tier price */}
              {step === 1 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-800">{language === 'hi' ? '1️⃣ आपके स्कूल में कितने छात्र हैं?' : '1️⃣ How many students in your school?'}</h3>

                  <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm text-green-800 flex items-center gap-2" data-testid="free-platform-banner">
                    ✅ <span>{language === 'hi'
                      ? 'Platform 100% FREE! सिर्फ़ one-time license/setup + सालाना maintenance। कोई per-student charge नहीं।'
                      : 'Platform 100% FREE! Only a one-time license/setup + yearly maintenance. No per-student charge.'}</span>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{language === 'hi' ? 'छात्रों की संख्या' : 'Number of students'}</label>
                    <input
                      type="number"
                      min="1"
                      value={studentCount}
                      onChange={(e) => setStudentCount(e.target.value.replace(/[^0-9]/g, ''))}
                      className="w-full px-4 py-4 border-2 rounded-xl text-2xl font-bold text-center focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder={language === 'hi' ? 'जैसे: 250' : 'e.g. 250'}
                      data-testid="student-count-input"
                    />
                    {tier && (
                      <p className="text-xs text-gray-500 mt-2 text-center" data-testid="tier-label">
                        {language === 'hi' ? 'आपका plan: ' : 'Your plan: '}<b>{language === 'hi' ? tier.label_hi : tier.label_en}</b>
                      </p>
                    )}
                  </div>

                  {/* Price / Custom box */}
                  {isCustom ? (
                    <div className="bg-slate-900 text-white rounded-xl p-5" data-testid="custom-tier-box">
                      <p className="font-semibold text-amber-300 mb-1">{language === 'hi' ? '🏛️ बड़े स्कूल (5000+ छात्र)' : '🏛️ Large school (5000+ students)'}</p>
                      <p className="text-sm text-gray-300">{language === 'hi' ? 'आपके स्कूल के लिए custom pricing है। हमारी sales team आपको सबसे अच्छा plan देगी।' : 'Custom pricing for your school. Our sales team will get you the best plan.'}</p>
                    </div>
                  ) : (
                    <div className="bg-gray-900 text-white rounded-xl p-5" data-testid="order-total-box">
                      <div className="flex justify-between items-center text-sm text-gray-300">
                        <span>{language === 'hi' ? 'One-time (license + setup)' : 'One-time (license + setup)'}</span>
                        <span data-testid="one-time-amount">{count > 0 ? fmt(oneTime) : '—'}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm text-gray-300 mt-1">
                        <span>{language === 'hi' ? 'सालाना maintenance' : 'Annual maintenance'}</span>
                        <span data-testid="maintenance-amount">{count > 0 ? `${fmt(maint)}${language === 'hi' ? '/साल' : '/year'}` : '—'}</span>
                      </div>
                      <div className="border-t border-gray-700 mt-3 pt-3 flex justify-between items-center">
                        <span className="text-gray-300">{language === 'hi' ? 'अभी देय (पहला भुगतान)' : 'Payable now (first payment)'}</span>
                        <span className="text-3xl font-bold text-green-400" data-testid="order-total-amount">{count > 0 ? fmt(total) : '—'}</span>
                      </div>
                      <p className="text-[11px] text-gray-400 mt-2">{language === 'hi' ? 'Software 100% FREE · Schools अपने API keys लाते हैं (BYOK)। अगले साल से सिर्फ़ maintenance।' : 'Software 100% FREE · Schools bring their own API keys (BYOK). Only maintenance from next year.'}</p>
                    </div>
                  )}

                  {isCustom ? (
                    <button
                      onClick={handleContactSales}
                      className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-bold text-lg hover:from-green-600 hover:to-emerald-700 transition flex items-center justify-center gap-2"
                      data-testid="contact-sales-btn"
                    >
                      <i className="fa-brands fa-whatsapp"></i>
                      {language === 'hi' ? 'Sales से संपर्क करें' : 'Contact Sales'}
                    </button>
                  ) : (
                    <button
                      onClick={() => setStep(2)}
                      disabled={!canContinueStep1}
                      className="w-full py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-bold text-lg hover:from-orange-600 hover:to-amber-600 transition disabled:opacity-50"
                      data-testid="step1-continue-btn"
                    >
                      {language === 'hi' ? 'आगे बढ़ें →' : 'Continue →'}
                    </button>
                  )}
                </div>
              )}

              {/* Step 2: School Details */}
              {step === 2 && (
                <div className="space-y-5">
                  <h3 className="text-lg font-semibold text-gray-800">{language === 'hi' ? '2️⃣ स्कूल की जानकारी' : '2️⃣ School Details'}</h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'hi' ? 'स्कूल का नाम *' : 'School Name *'}</label>
                    <input type="text" value={formData.schoolName} onChange={(e) => setFormData({ ...formData, schoolName: e.target.value })} className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder={language === 'hi' ? 'जैसे: Delhi Public School' : 'e.g. Delhi Public School'} data-testid="school-name-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'hi' ? 'संपर्क व्यक्ति का नाम *' : 'Contact Person Name *'}</label>
                    <input type="text" value={formData.contactName} onChange={(e) => setFormData({ ...formData, contactName: e.target.value })} className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder={language === 'hi' ? 'आपका नाम' : 'Your name'} data-testid="contact-name-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'hi' ? 'WhatsApp नंबर *' : 'WhatsApp Number *'}</label>
                    <input type="tel" value={formData.contactPhone} onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })} className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="9876543210" data-testid="contact-phone-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'hi' ? 'Email (Optional)' : 'Email (Optional)'}</label>
                    <input type="email" value={formData.contactEmail} onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })} className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="school@example.com" data-testid="contact-email-input" />
                  </div>

                  {/* Order Summary */}
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                    <h4 className="font-semibold text-orange-800 mb-2">{language === 'hi' ? '📋 Order Summary' : '📋 Order Summary'}</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between"><span className="text-gray-600">{language === 'hi' ? 'छात्र:' : 'Students:'}</span><span className="font-medium">{count} ({language === 'hi' ? tier?.label_hi : tier?.label_en})</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">{language === 'hi' ? 'One-time (license+setup):' : 'One-time (license+setup):'}</span><span className="font-medium">{fmt(oneTime)}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">{language === 'hi' ? 'सालाना maintenance:' : 'Annual maintenance:'}</span><span className="font-medium">{fmt(maint)}{language === 'hi' ? '/साल' : '/year'}</span></div>
                      <hr className="my-2" />
                      <div className="flex justify-between text-lg font-bold text-orange-700"><span>{language === 'hi' ? 'अभी देय:' : 'Payable now:'}</span><span data-testid="summary-total">{fmt(total)}</span></div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button onClick={() => setStep(1)} className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-100 transition">← {language === 'hi' ? 'पीछे' : 'Back'}</button>
                    <button onClick={() => setStep(3)} disabled={!formData.schoolName || !formData.contactName || !formData.contactPhone} className="flex-1 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-semibold hover:from-orange-600 hover:to-amber-600 transition disabled:opacity-50">{language === 'hi' ? 'भुगतान करें →' : 'Proceed to Pay →'}</button>
                  </div>
                </div>
              )}

              {/* Step 3: Payment */}
              {step === 3 && (
                <div className="space-y-5">
                  <h3 className="text-lg font-semibold text-gray-800">{language === 'hi' ? '3️⃣ भुगतान' : '3️⃣ Payment'}</h3>

                  <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white rounded-2xl p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="text-4xl">🏫</div>
                      <div>
                        <h4 className="text-xl font-bold">{language === 'hi' ? 'Shiksha Mitra Subscription' : 'Shiksha Mitra Subscription'}</h4>
                        <p className="text-gray-400 text-sm">{formData.schoolName} · {count} {language === 'hi' ? 'छात्र' : 'students'}</p>
                      </div>
                    </div>
                    <div className="border-t border-gray-700 pt-4 mt-4 space-y-1 text-sm text-gray-300">
                      <div className="flex justify-between"><span>{language === 'hi' ? 'One-time (license + setup)' : 'One-time (license + setup)'}</span><span>{fmt(oneTime)}</span></div>
                      <div className="flex justify-between"><span>{language === 'hi' ? 'पहले साल का maintenance' : 'First-year maintenance'}</span><span>{fmt(maint)}</span></div>
                    </div>
                    <div className="border-t border-gray-700 pt-4 mt-3 flex justify-between items-center">
                      <span className="text-gray-400">{language === 'hi' ? 'कुल देय राशि' : 'Total Amount to Pay'}</span>
                      <span className="text-3xl font-bold text-green-400">{fmt(total)}</span>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-sm text-gray-600 mb-3 font-medium">{language === 'hi' ? '💳 Supported Payment Methods:' : '💳 Supported Payment Methods:'}</p>
                    <div className="flex flex-wrap gap-3">
                      {['UPI', 'Credit Card', 'Debit Card', 'Net Banking', 'Wallets'].map((m) => (<span key={m} className="px-3 py-1.5 bg-white border rounded-full text-sm text-gray-600">{m}</span>))}
                    </div>
                  </div>

                  {paymentError && (<div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">⚠️ {paymentError}</div>)}

                  <div className="flex gap-3">
                    <button onClick={() => setStep(2)} className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-100 transition">← {language === 'hi' ? 'पीछे' : 'Back'}</button>
                    <button onClick={handlePayment} disabled={loading} className="flex-1 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-bold text-lg hover:from-green-600 hover:to-emerald-700 transition disabled:opacity-50 flex items-center justify-center gap-2" data-testid="pay-now-btn">
                      {loading ? (<><span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></span>{language === 'hi' ? 'प्रोसेसिंग...' : 'Processing...'}</>) : (language === 'hi' ? `${fmt(total)} Pay करें` : `Pay ${fmt(total)}`)}
                    </button>
                  </div>

                  <div className="flex items-center justify-center gap-2">
                    <img src="https://razorpay.com/favicon.png" alt="Razorpay" className="h-5 w-5" />
                    <p className="text-xs text-gray-500">{language === 'hi' ? '🔒 100% सुरक्षित भुगतान Razorpay के माध्यम से' : '🔒 100% Secure Payment via Razorpay'}</p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
